/**
 * Custom-field suggestions Flux Agent distilled from the current conversation, shown as cards at
 * the end of the chat with Add and Dismiss. Extraction runs in the background after a turn, so the
 * list is polled a few times after each turn (`refreshKey` changes). Suggestions that already
 * appear as a `suggest_custom_field` action card in the thread are not repeated.
 *
 * @module apps/web/src/components/flux-agent/learning/FluxAgentSuggestionCards
 */

import { useEffect, useMemo, useState } from 'react';
import { Lightbulb, Loader2, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { FluxAgentMessage } from '@/lib/fluxAgentApi';
import { fluxAgentLearningApi, type FluxAgentInsight } from '@/lib/fluxAgentLearningApi';
import { useApplySuggestion } from './useApplySuggestion';

/** Delays after a turn at which suggestions are re-fetched, in milliseconds. */
const POLL_DELAYS_MS = [1_500, 5_000, 12_000];

/** Inputs for {@link FluxAgentSuggestionCards}. */
export interface FluxAgentSuggestionCardsProps {
  /** Conversation whose suggestions are shown; renders nothing without one. */
  conversationId: string | null;
  /** Visible thread, used to skip suggestions already shown as action cards. */
  messages: FluxAgentMessage[];
  /** Changes after every completed turn to trigger polling. */
  refreshKey: number;
}

/** Shape that selects the field-suggestion member of the insight union. */
interface FieldSuggestionKind {
  /** Discriminant of field-suggestion insights. */
  kind: 'field_suggestion';
}

/** Field-suggestion insight narrowed from the union. */
type SuggestionInsight = Extract<FluxAgentInsight, FieldSuggestionKind>;

/** Show active field suggestions for the conversation with one-tap Add and Dismiss. */
export function FluxAgentSuggestionCards({
  conversationId,
  messages,
  refreshKey,
}: FluxAgentSuggestionCardsProps) {
  const [suggestions, setSuggestions] = useState<SuggestionInsight[]>([]);
  const { outcomes, add, dismiss } = useApplySuggestion();

  useEffect(() => {
    if (!conversationId) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      const result = await fluxAgentLearningApi.listInsights({
        kind: 'field_suggestion',
        status: 'active',
      });
      if (cancelled || !result.ok) return;
      setSuggestions(
        result.data.insights.filter(
          (insight): insight is SuggestionInsight =>
            insight.kind === 'field_suggestion' && insight.conversationId === conversationId
        )
      );
    };
    void load();
    const timers =
      refreshKey > 0 ? POLL_DELAYS_MS.map(delay => window.setTimeout(load, delay)) : [];
    return () => {
      cancelled = true;
      timers.forEach(timer => window.clearTimeout(timer));
    };
  }, [conversationId, refreshKey]);

  const shownAsActions = useMemo(
    () =>
      new Set(
        messages.flatMap(message =>
          (message.actions || []).flatMap(action => {
            const id = (action as { insightId?: string }).insightId;
            return id ? [id] : [];
          })
        )
      ),
    [messages]
  );

  const visible = suggestions.filter(
    suggestion => !shownAsActions.has(suggestion.id) && outcomes[suggestion.id] !== 'dismissed'
  );
  if (!visible.length) return null;

  return (
    <ul className="mt-4 space-y-2" aria-label="Suggested fields">
      {visible.map(suggestion => {
        const outcome = outcomes[suggestion.id];
        const { payload } = suggestion;
        return (
          <li
            key={suggestion.id}
            className="border-primary/30 bg-primary/5 flex flex-col gap-2 rounded-lg border px-3 py-2.5 text-sm sm:flex-row sm:items-center"
          >
            <Lightbulb
              className="text-primary hidden h-4 w-4 shrink-0 sm:block"
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <p className="font-medium">Track “{payload.name}”?</p>
              <p className="text-muted-foreground break-words text-xs">{payload.question}</p>
              {payload.rationale && (
                <p className="text-muted-foreground break-words text-xs">{payload.rationale}</p>
              )}
            </div>
            {outcome === 'added' ? (
              <p className="text-primary text-xs font-medium" role="status">
                Added
              </p>
            ) : (
              <div className="flex shrink-0 gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  className="h-8 gap-1"
                  disabled={outcome === 'pending'}
                  onClick={() => void add(suggestion.id, payload.name)}
                  aria-label={`Add field ${payload.name}`}
                >
                  {outcome === 'pending' ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Plus className="h-3.5 w-3.5" />
                  )}
                  Add
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 gap-1"
                  disabled={outcome === 'pending'}
                  onClick={() => void dismiss(suggestion.id)}
                  aria-label={`Dismiss field ${payload.name}`}
                >
                  <X className="h-3.5 w-3.5" /> Dismiss
                </Button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
